import React, { useMemo } from "react"
import { Button } from "@/components/ui/button"

// Configuration for hero section content
const HERO_CONFIG = {
  userCount: "10,000+", // Can be updated based on actual metrics
  dailyTimeSavings: "2+", // Hours saved per day
} as const;

interface HeroSectionProps {
  onGetStartedClick: () => void
  onWatchDemoClick?: () => void
}

export default function HeroSection({ onGetStartedClick, onWatchDemoClick }: HeroSectionProps) {
  // Memoize particle styles to prevent recalculation on every render
  const particleStyles = useMemo(() => {
    return Array.from({ length: 20 }, () => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${3 + Math.random() * 4}s`,
    }));
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 pt-20 md:pt-0">
      {/* Animated CSS Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 via-blue-500/10 to-purple-500/10 animate-pulse" />

        {/* Floating particles effect with CSS */}
        <div className="absolute inset-0 overflow-hidden">
          {particleStyles.map((style, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-teal-400/30 rounded-full animate-float"
              style={style}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl z-10 relative">
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight font-geist-bold">
          Automate Your Day. <br /> Reclaim Your Time.
        </h1>
        <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
          Save {HERO_CONFIG.dailyTimeSavings} hours daily with AI that handles email responses, schedules meetings, and manages your tasks automatically.
          Join {HERO_CONFIG.userCount} users who've reclaimed their time.
        </p>

        {/* Use Cases Highlight */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-10 max-w-3xl mx-auto">
          <div className="flex items-center text-sm md:text-base text-teal-300 bg-teal-500/10 px-3 py-2 rounded-full border border-teal-500/20">
            <span className="w-2 h-2 bg-teal-400 rounded-full mr-2"></span>
            Automatically respond to routine emails
          </div>
          <div className="flex items-center text-sm md:text-base text-teal-300 bg-teal-500/10 px-3 py-2 rounded-full border border-teal-500/20">
            <span className="w-2 h-2 bg-teal-400 rounded-full mr-2"></span>
            Schedule meetings without back-and-forth
          </div>
          <div className="flex items-center text-sm md:text-base text-teal-300 bg-teal-500/10 px-3 py-2 rounded-full border border-teal-500/20">
            <span className="w-2 h-2 bg-teal-400 rounded-full mr-2"></span>
            Prioritize and organize daily tasks
          </div>
        </div>
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            size="lg"
            className="bg-teal-400 hover:bg-teal-500 text-slate-900 font-semibold px-10 py-6 text-lg transition-all duration-200 hover:scale-105"
            onClick={onGetStartedClick}
            data-analytics-event="hero_cta_primary_click"
            data-analytics-location="hero_section"
            data-analytics-cta-type="primary"
            data-analytics-cta-text="Get Started for Free"
          >
            Get Started for Free
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 font-semibold px-10 py-6 text-lg transition-all duration-200 hover:scale-105 backdrop-blur-sm"
            onClick={onWatchDemoClick}
            data-analytics-event="hero_cta_secondary_click"
            data-analytics-location="hero_section"
            data-analytics-cta-type="secondary"
            data-analytics-cta-text="Watch Demo"
          >
            Watch Demo
          </Button>
        </div>
      </div>
    </section>
  )
}
